import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { searchPlayers } from '../../firebase/players';
import { getUserProfile } from '../../firebase/auth';
import { formatPhoneInput, validatePhone, maskPhone } from '../../utils/phoneUtils';
import { getCurrentOrNextOccurrence, formatLocalDateTime } from '../../utils/recurringUtils';

export default function PlayersModal({
    isOpen,
    onClose,
    playersText,
    handlePlayersChange,
    handlePlayersBlur,
    currentPlayers: currentPlayersProp, // Renamed to avoid confusion
    tournament,
    playerPayments,
    handlePaymentToggle,
    handleSidebarPlayerNameChange,
    bracket,
    playerPhones = {},
    onPlayerPhoneChange,
    playerUserIds = {},
    onPlayerUserIdChange,
    selectedInstanceDate = null
}) {
    const [editingPlayer, setEditingPlayer] = useState(null);
    const [editValue, setEditValue] = useState('');
    const [editPlayer1Value, setEditPlayer1Value] = useState(''); // For doubles editing
    const [editPlayer2Value, setEditPlayer2Value] = useState(''); // For doubles editing
    const [editPhoneValue, setEditPhoneValue] = useState('');
    const [editError, setEditError] = useState('');
    const [addingNewPlayer, setAddingNewPlayer] = useState(false);
    const [newPlayerValue, setNewPlayerValue] = useState('');
    const [newPlayerError, setNewPlayerError] = useState('');
    const [newPlayerPhone, setNewPlayerPhone] = useState('');
    const [newPlayerPhoneError, setNewPlayerPhoneError] = useState('');
    const isSortingRef = useRef(false);

    // Callback ref attached to the add-player/add-team form wrapper. Whenever
    // the form mounts (i.e. the director just clicked "Add Player"/"Add Team"),
    // scroll it into view so the input + buttons aren't clipped below the
    // long player list. The callback only fires on mount/unmount, not on
    // every keystroke, because the function identity is stable via useCallback.
    const addPlayerFormRef = useCallback((el) => {
        if (!el) return;
        requestAnimationFrame(() => {
            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        });
    }, []);

    // Check if self-scoring is enabled (requires phone numbers)
    const selfScoringEnabled = tournament?.selfScoringEnabled || false;

    // Singles autocomplete state
    const [singlesSearchResults, setSinglesSearchResults] = useState([]);
    const [isSearchingSingles, setIsSearchingSingles] = useState(false);
    const [showSinglesDropdown, setShowSinglesDropdown] = useState(false);
    const singlesSearchTimeoutRef = useRef(null);
    const [selectedSinglesUserId, setSelectedSinglesUserId] = useState(null); // Track selected singles player's user ID

    // Doubles/Scotch Doubles state
    const [newPlayer1Value, setNewPlayer1Value] = useState('');
    const [newPlayer2Value, setNewPlayer2Value] = useState('');
    const [selectedPlayer1UserId, setSelectedPlayer1UserId] = useState(null); // Track selected player's user ID
    const [selectedPlayer2UserId, setSelectedPlayer2UserId] = useState(null); // Track selected player's user ID
    const [player1SearchResults, setPlayer1SearchResults] = useState([]);
    const [player2SearchResults, setPlayer2SearchResults] = useState([]);
    const [isSearchingPlayer1, setIsSearchingPlayer1] = useState(false);
    const [isSearchingPlayer2, setIsSearchingPlayer2] = useState(false);
    const [showPlayer1Dropdown, setShowPlayer1Dropdown] = useState(false);
    const [showPlayer2Dropdown, setShowPlayer2Dropdown] = useState(false);
    const player1SearchTimeoutRef = useRef(null);
    const player2SearchTimeoutRef = useRef(null);
    const [teamPhone, setTeamPhone] = useState('');
    const [teamPhoneError, setTeamPhoneError] = useState('');

    // Check if this is a doubles format tournament
    const isDoublesFormat = tournament?.playerFormat === 'Doubles' || tournament?.playerFormat === 'Scotch Doubles';

    // Derive currentPlayers from playersText directly to ensure immediate updates
    // When bracket exists, use the prop (which comes from bracket data), otherwise derive from playersText
    const hasBracket = Array.isArray(bracket) && bracket.length > 0;
    const currentPlayers = useMemo(() => {
        if (hasBracket) {
            // When tournament is active, use prop which is derived from bracket
            return currentPlayersProp;
        }
        // Before tournament starts, derive from playersText for immediate reactivity
        return playersText
            .trim()
            .split('\n')
            .filter(Boolean)
            .map(name => name.trim())
            .filter(name => name);
    }, [hasBracket, currentPlayersProp, playersText]);

    // Bulk add section state
    const [showBulkSection, setShowBulkSection] = useState(false);

    // Get registered players for this tournament instance who aren't already in the player list
    const registeredPlayersNotInList = useMemo(() => {
        if (!tournament?.registrations?.registeredUsers) return [];

        // Determine the target instance date for filtering registrations
        let targetInstanceDate = null;
        if (tournament.recurring?.enabled) {
            // For recurring tournaments, use selected instance or current/next occurrence
            // Use formatLocalDateTime to avoid UTC conversion issues
            if (selectedInstanceDate) {
                targetInstanceDate = selectedInstanceDate;
            } else {
                const currentOrNext = getCurrentOrNextOccurrence(tournament.recurring);
                targetInstanceDate = currentOrNext ? formatLocalDateTime(currentOrNext) : null;
            }
        }

        // Filter registrations for this instance
        const registrations = tournament.registrations.registeredUsers.filter(reg => {
            if (!targetInstanceDate) {
                // Non-recurring: show registrations without instanceDate
                return !reg.instanceDate;
            }
            // Recurring: show registrations for this specific instance
            return reg.instanceDate === targetInstanceDate;
        });

        // Filter out players already in the list
        return registrations.filter(reg => {
            // For doubles, format as "Player & Partner"
            const playerName = isDoublesFormat && reg.partnerName
                ? `${reg.displayName} & ${reg.partnerName}`
                : reg.displayName;

            // Check if this player name is already in the list (case-insensitive)
            return !currentPlayers.some(p =>
                p.toLowerCase() === playerName.toLowerCase()
            );
        });
    }, [tournament?.registrations?.registeredUsers, tournament?.recurring, selectedInstanceDate, currentPlayers, isDoublesFormat]);

    // Doubles format: Search for Player 1
    useEffect(() => {
        if (!isDoublesFormat || !tournament?.isPublic) return;

        if (newPlayer1Value.trim().length < 2) {
            setPlayer1SearchResults([]);
            return;
        }

        if (player1SearchTimeoutRef.current) {
            clearTimeout(player1SearchTimeoutRef.current);
        }

        player1SearchTimeoutRef.current = setTimeout(async () => {
            setIsSearchingPlayer1(true);
            try {
                const results = await searchPlayers(newPlayer1Value.trim(), 5);
                setPlayer1SearchResults(results);
            } catch (error) {
                setPlayer1SearchResults([]);
            }
            setIsSearchingPlayer1(false);
        }, 300);

        return () => {
            if (player1SearchTimeoutRef.current) {
                clearTimeout(player1SearchTimeoutRef.current);
            }
        };
    }, [newPlayer1Value, isDoublesFormat, tournament?.isPublic]);

    // Doubles format: Search for Player 2
    useEffect(() => {
        if (!isDoublesFormat || !tournament?.isPublic) return;

        if (newPlayer2Value.trim().length < 2) {
            setPlayer2SearchResults([]);
            return;
        }

        if (player2SearchTimeoutRef.current) {
            clearTimeout(player2SearchTimeoutRef.current);
        }

        player2SearchTimeoutRef.current = setTimeout(async () => {
            setIsSearchingPlayer2(true);
            try {
                const results = await searchPlayers(newPlayer2Value.trim(), 5);
                setPlayer2SearchResults(results);
            } catch (error) {
                setPlayer2SearchResults([]);
            }
            setIsSearchingPlayer2(false);
        }, 300);

        return () => {
            if (player2SearchTimeoutRef.current) {
                clearTimeout(player2SearchTimeoutRef.current);
            }
        };
    }, [newPlayer2Value, isDoublesFormat, tournament?.isPublic]);

    // Singles format: Search for player as user types in Add Player input
    useEffect(() => {
        if (isDoublesFormat || !tournament?.isPublic) return;

        if (newPlayerValue.trim().length < 2) {
            setSinglesSearchResults([]);
            return;
        }

        if (singlesSearchTimeoutRef.current) {
            clearTimeout(singlesSearchTimeoutRef.current);
        }

        singlesSearchTimeoutRef.current = setTimeout(async () => {
            setIsSearchingSingles(true);
            try {
                const results = await searchPlayers(newPlayerValue.trim(), 5);
                // Filter out players already in the list
                const filteredResults = results.filter(player =>
                    !currentPlayers.some(p => p.toLowerCase() === player.displayName?.toLowerCase())
                );
                setSinglesSearchResults(filteredResults);
            } catch (error) {
                setSinglesSearchResults([]);
            }
            setIsSearchingSingles(false);
        }, 300);

        return () => {
            if (singlesSearchTimeoutRef.current) {
                clearTimeout(singlesSearchTimeoutRef.current);
            }
        };
    }, [newPlayerValue, isDoublesFormat, tournament?.isPublic, currentPlayers]);

    // Select a player from autocomplete for singles
    const selectSinglesPlayer = async (player) => {
        const userId = player.odUserId || player.id || null;
        console.log('[PlayerUserIds] Singles player selected from autocomplete:', {
            displayName: player.displayName,
            odUserId: player.odUserId,
            id: player.id,
            resolvedUserId: userId
        });
        setNewPlayerValue(player.displayName);
        setSelectedSinglesUserId(userId); // Store the user ID
        setSinglesSearchResults([]);
        setShowSinglesDropdown(false);

        // If self-scoring is enabled, try to auto-populate the phone number from the user's profile
        if (selfScoringEnabled && player.odUserId) {
            try {
                const { profile } = await getUserProfile(player.odUserId);
                if (profile?.phone) {
                    // Format the phone number for display
                    const digits = profile.phone.replace(/\D/g, '');
                    let formatted = digits;
                    if (digits.length >= 6) {
                        formatted = `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
                    } else if (digits.length >= 3) {
                        formatted = `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
                    }
                    setNewPlayerPhone(formatted);
                    setNewPlayerPhoneError('');
                }
            } catch (error) {
                console.log('Could not fetch user profile for phone auto-fill:', error);
            }
        }
    };

    // Select a player from autocomplete for doubles
    const selectPlayer1 = (player) => {
        const userId = player.odUserId || player.id || null;
        console.log('[PlayerUserIds] Doubles player 1 selected from autocomplete:', {
            displayName: player.displayName,
            odUserId: player.odUserId,
            id: player.id,
            resolvedUserId: userId
        });
        setNewPlayer1Value(player.displayName);
        setSelectedPlayer1UserId(userId); // Store the user ID
        setPlayer1SearchResults([]);
        setShowPlayer1Dropdown(false);
    };

    const selectPlayer2 = (player) => {
        const userId = player.odUserId || player.id || null;
        console.log('[PlayerUserIds] Doubles player 2 selected from autocomplete:', {
            displayName: player.displayName,
            odUserId: player.odUserId,
            id: player.id,
            resolvedUserId: userId
        });
        setNewPlayer2Value(player.displayName);
        setSelectedPlayer2UserId(userId); // Store the user ID
        setPlayer2SearchResults([]);
        setShowPlayer2Dropdown(false);
    };

    // Helper function to capitalize a name
    const capitalizeName = (name) => {
        return name.split(' ').map(word =>
            word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
        ).join(' ');
    };

    // Submit doubles team
    const submitDoublesTeam = () => {
        const player1 = newPlayer1Value.trim();
        const player2 = newPlayer2Value.trim();

        if (!player1 || !player2) {
            setNewPlayerError('Both player names are required');
            return;
        }

        // Validate phone if self-scoring is enabled AND a phone number is provided
        // Phone is optional - players without phones won't receive SMS notifications
        if (selfScoringEnabled && teamPhone.trim()) {
            const phoneValidation = validatePhone(teamPhone);
            if (!phoneValidation.valid) {
                setTeamPhoneError(phoneValidation.error);
                return;
            }
        }

        // Capitalize names
        const capitalizedPlayer1 = capitalizeName(player1);
        const capitalizedPlayer2 = capitalizeName(player2);

        // Create team name with & separator
        const teamName = `${capitalizedPlayer1} & ${capitalizedPlayer2}`;

        // Check for duplicates
        if (currentPlayers.some(p => p.toLowerCase() === teamName.toLowerCase())) {
            setNewPlayerError('This team already exists');
            return;
        }

        // If self-scoring is enabled and phone provided, save the phone number for the team
        if (selfScoringEnabled && onPlayerPhoneChange && teamPhone.trim()) {
            onPlayerPhoneChange(teamName, {
                phone: teamPhone.replace(/\D/g, ''), // Store digits only
                phoneMasked: maskPhone(teamPhone)
            });
        }

        // Save user IDs for the team members if they were selected from autocomplete
        console.log('[PlayerUserIds] submitDoublesTeam - checking userId save:', {
            hasOnPlayerUserIdChange: !!onPlayerUserIdChange,
            selectedPlayer1UserId,
            selectedPlayer2UserId,
            teamName
        });
        if (onPlayerUserIdChange && (selectedPlayer1UserId || selectedPlayer2UserId)) {
            // Store the first available user ID for the team (used for profile viewing)
            // Also store individual player mappings for lookup
            const userIdData = {
                odUserId: selectedPlayer1UserId || selectedPlayer2UserId, // Primary ID for the team
                player1UserId: selectedPlayer1UserId,
                player2UserId: selectedPlayer2UserId
            };
            console.log('[PlayerUserIds] Saving doubles team userIds:', teamName, '->', userIdData);
            onPlayerUserIdChange(teamName, userIdData);
        }

        // Add the team to the list
        const updatedPlayers = [...currentPlayers, teamName];
        const updatedText = updatedPlayers.join('\n');
        handlePlayersChange({ target: { value: updatedText } });

        // Reset state
        setNewPlayer1Value('');
        setNewPlayer2Value('');
        setSelectedPlayer1UserId(null);
        setSelectedPlayer2UserId(null);
        setNewPlayerError('');
        setTeamPhone('');
        setTeamPhoneError('');
        setAddingNewPlayer(false);
        setPlayer1SearchResults([]);
        setPlayer2SearchResults([]);
    };

    // Cancel adding doubles team
    const cancelAddingDoublesTeam = () => {
        setNewPlayer1Value('');
        setNewPlayer2Value('');
        setNewPlayerError('');
        setTeamPhone('');
        setTeamPhoneError('');
        setAddingNewPlayer(false);
        setPlayer1SearchResults([]);
        setPlayer2SearchResults([]);
    };

    if (!isOpen) return null;

    // hasBracket is already computed above in useMemo

    // Sort players alphabetically
    const handleSortAlphabetically = () => {
        // Set flag to prevent blur handler from interfering
        isSortingRef.current = true;

        // Get the current text from the textarea
        const currentText = playersText.trim();
        const players = currentText.split('\n').filter(Boolean).map(name => name.trim());

        // Sort alphabetically (case-insensitive) and capitalize names
        const sortedPlayers = [...players].sort((a, b) =>
            a.toLowerCase().localeCompare(b.toLowerCase())
        );

        // Manually capitalize names (same logic as handlePlayersBlur)
        const capitalizedSortedPlayers = sortedPlayers.map(name => {
            return name.split(' ').map(word =>
                word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
            ).join(' ');
        });

        const sortedText = capitalizedSortedPlayers.join('\n');

        // Update the text directly
        handlePlayersChange({ target: { value: sortedText } });

        // Reset flag after a short delay to allow state to settle
        setTimeout(() => {
            isSortingRef.current = false;
        }, 100);
    };

    // Wrapper for blur handler that checks if we're sorting
    const handleTextareaBlur = () => {
        if (isSortingRef.current) {
            return; // Skip blur during sort
        }
        handlePlayersBlur();
    };

    // Start editing a player name (and phone if self-scoring enabled)
    const startEditing = (playerName) => {
        setEditingPlayer(playerName);
        setEditValue(playerName);

        // For doubles, parse the team name into two players
        if (isDoublesFormat && playerName.includes(' & ')) {
            const [player1, player2] = playerName.split(' & ');
            setEditPlayer1Value(player1 || '');
            setEditPlayer2Value(player2 || '');
        } else {
            setEditPlayer1Value('');
            setEditPlayer2Value('');
        }

        // Pre-fill phone if available (format for display)
        const existingPhone = playerPhones[playerName]?.phone || '';
        if (existingPhone) {
            // Format as (XXX) XXX-XXXX for display
            const digits = existingPhone.replace(/\D/g, '');
            if (digits.length === 10) {
                setEditPhoneValue(`(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`);
            } else {
                setEditPhoneValue(existingPhone);
            }
        } else {
            setEditPhoneValue('');
        }
        setEditError('');
    };

    // Cancel editing
    const cancelEditing = () => {
        setEditingPlayer(null);
        setEditValue('');
        setEditPlayer1Value('');
        setEditPlayer2Value('');
        setEditPhoneValue('');
        setEditError('');
    };

    // Submit edit
    const submitEdit = (oldName, playerIndex) => {
        let newName;

        // For doubles, combine the two player names
        if (isDoublesFormat) {
            const p1 = editPlayer1Value.trim();
            const p2 = editPlayer2Value.trim();

            if (!p1 || !p2) {
                setEditError('Both player names are required');
                return;
            }

            newName = `${p1} & ${p2}`;
        } else {
            newName = editValue.trim();
        }

        if (!newName) {
            setEditError('Player name cannot be empty');
            return;
        }

        const nameChanged = newName !== oldName;

        // Check for duplicates only if name changed
        if (nameChanged) {
            const isDuplicate = currentPlayers.some(p =>
                p !== oldName && p.toLowerCase() === newName.toLowerCase()
            );

            if (isDuplicate) {
                setEditError('Player name already exists');
                return;
            }
        }

        // Validate phone if provided and self-scoring is enabled
        if (selfScoringEnabled && editPhoneValue.trim()) {
            const phoneValidation = validatePhone(editPhoneValue);
            if (!phoneValidation.valid) {
                setEditError(phoneValidation.error);
                return;
            }
        }

        // Handle phone updates when self-scoring is enabled
        if (selfScoringEnabled && onPlayerPhoneChange) {
            const targetName = nameChanged ? newName : oldName;

            if (editPhoneValue.trim()) {
                // Save/update phone
                onPlayerPhoneChange(targetName, {
                    phone: editPhoneValue.replace(/\D/g, ''),
                    phoneMasked: maskPhone(editPhoneValue)
                });
            } else if (playerPhones[oldName]) {
                // Phone was cleared - remove it
                if (nameChanged) {
                    // Just don't copy to new name
                } else {
                    onPlayerPhoneChange(oldName, null);
                }
            }

            // If name changed and had phone, remove old entry
            if (nameChanged && playerPhones[oldName] && editPhoneValue.trim()) {
                onPlayerPhoneChange(oldName, null);
            }
        }

        // Call the parent handler for string players only if name changed
        if (nameChanged && handleSidebarPlayerNameChange) {
            handleSidebarPlayerNameChange(oldName, newName, playerIndex);
        }

        cancelEditing();
    };

    // Handle keypress in edit input
    const handleKeyPress = (e, playerName, playerIndex) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            submitEdit(playerName, playerIndex);
        } else if (e.key === 'Escape') {
            cancelEditing();
        }
    };

    // Remove player
    const handleRemovePlayer = (playerName) => {
        const updatedPlayers = currentPlayers.filter(p => p !== playerName);
        const newText = updatedPlayers.join('\n');
        handlePlayersChange({ target: { value: newText } });
    };

    // Start adding new player
    const startAddingPlayer = () => {
        setAddingNewPlayer(true);
        setNewPlayerValue('');
        setNewPlayerError('');
    };

    // Cancel adding new player
    const cancelAddingPlayer = () => {
        setAddingNewPlayer(false);
        setNewPlayerValue('');
        setNewPlayerError('');
        setNewPlayerPhone('');
        setNewPlayerPhoneError('');
        setSinglesSearchResults([]);
        setShowSinglesDropdown(false);
        setSelectedSinglesUserId(null); // Reset selected user ID
    };

    // Submit new player
    const submitNewPlayer = () => {
        const newName = newPlayerValue.trim();

        if (!newName) {
            setNewPlayerError('Player name cannot be empty');
            return;
        }

        // Validate phone if self-scoring is enabled AND a phone number is provided
        // Phone is optional - players without phones won't receive SMS notifications
        if (selfScoringEnabled && newPlayerPhone.trim()) {
            const phoneValidation = validatePhone(newPlayerPhone);
            if (!phoneValidation.valid) {
                setNewPlayerPhoneError(phoneValidation.error);
                return;
            }
        }

        // Check for duplicates
        const playerNames = currentPlayers.map(p => p.toLowerCase());
        if (playerNames.some(name => name === newName.toLowerCase())) {
            setNewPlayerError('Player name already exists');
            return;
        }

        // Capitalize the name
        const capitalizedName = newName.split(' ').map(word =>
            word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
        ).join(' ');

        // If self-scoring is enabled and phone provided, save the phone number separately
        if (selfScoringEnabled && onPlayerPhoneChange && newPlayerPhone.trim()) {
            onPlayerPhoneChange(capitalizedName, {
                phone: newPlayerPhone.replace(/\D/g, ''), // Store digits only
                phoneMasked: maskPhone(newPlayerPhone)
            });
        }

        // Save user ID if player was selected from autocomplete
        console.log('[PlayerUserIds] submitNewPlayer - checking userId save:', {
            hasOnPlayerUserIdChange: !!onPlayerUserIdChange,
            selectedSinglesUserId,
            capitalizedName
        });
        if (onPlayerUserIdChange && selectedSinglesUserId) {
            console.log('[PlayerUserIds] Saving singles player userId:', capitalizedName, '->', selectedSinglesUserId);
            onPlayerUserIdChange(capitalizedName, selectedSinglesUserId);
        }

        // Add the new player to the list (always as a string)
        const updatedPlayers = [...currentPlayers, capitalizedName];
        const updatedText = updatedPlayers.join('\n');

        // Update the players text
        handlePlayersChange({ target: { value: updatedText } });

        // Reset state immediately (don't wait for blur)
        cancelAddingPlayer();
    };

    // Handle keypress in new player input
    const handleNewPlayerKeyPress = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            submitNewPlayer();
        } else if (e.key === 'Escape') {
            cancelAddingPlayer();
        }
    };

    // Calculate payment summary
    const totalPlayers = currentPlayers.length;
    const paidCount = currentPlayers.filter(p => playerPayments[p]?.paid).length;
    const totalExpected = totalPlayers * (tournament?.entryFee || 0);
    const totalCollected = paidCount * (tournament?.entryFee || 0);

    const modalContent = (
        <>
            {/* Modal Overlay */}
            <div
                style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'var(--bg-modal-overlay)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 9999,
                    padding: '20px'
                }}
            >
                {/* Modal Content */}
                <div
                    style={{
                        backgroundColor: 'var(--bg-card)',
                        borderRadius: 12,
                        maxWidth: '600px',
                        width: '100%',
                        maxHeight: '90vh',
                        display: 'flex',
                        flexDirection: 'column',
                        boxShadow: 'var(--shadow-lg)'
                    }}
                >
                    {/* Header */}
                    <div style={{
                        padding: '20px 24px',
                        borderBottom: '1px solid var(--border-color)',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                    }}>
                        <h2 style={{ margin: 0, fontSize: 20, color: 'var(--text-primary)' }}>
                            Manage {isDoublesFormat ? 'Teams' : 'Players'} {tournament?.entryFee > 0 && '& Payments'}
                        </h2>
                        <button
                            onClick={onClose}
                            style={{
                                background: 'none',
                                border: 'none',
                                fontSize: 24,
                                cursor: 'pointer',
                                color: 'var(--text-secondary)',
                                padding: '0 8px'
                            }}
                            title="Close"
                        >
                            ×
                        </button>
                    </div>

                    {/* Modal Body - Scrollable */}
                    <div style={{
                        padding: '24px',
                        overflowY: 'auto',
                        flex: 1
                    }}>
                        {/* Self-Scoring Info Banner */}
                        {selfScoringEnabled && (
                            <div style={{
                                marginBottom: 20,
                                padding: '12px 16px',
                                backgroundColor: 'var(--info-bg)',
                                border: '1px solid var(--info-border)',
                                borderRadius: 8,
                                fontSize: 13,
                                color: 'var(--info-text)'
                            }}>
                                <div style={{ fontWeight: 'bold', marginBottom: 4 }}>
                                    SMS Alerts & Self-Scoring Enabled
                                </div>
                                <div style={{ marginBottom: 8 }}>
                                    Phone numbers are optional. Players with phone numbers will receive SMS notifications when their match is ready and a link to self-score. Players without phone numbers can still play but won't receive text alerts. Before entering a phone number, obtain verbal consent: "By providing your phone number, you agree to receive text messages from Pool Tournaments including match notifications. Reply STOP to opt out. Do you agree?"
                                </div>
                                <div style={{ fontSize: 11, opacity: 0.85 }}>
                                    <a href="/terms" target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', textDecoration: 'underline' }}>Terms of Service</a>
                                    {' | '}
                                    <a href="/privacy" target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', textDecoration: 'underline' }}>Privacy Policy</a>
                                </div>
                            </div>
                        )}

                        {/* Registered Players Section - Show players who registered but aren't in the list yet */}
                        {!hasBracket && registeredPlayersNotInList.length > 0 && (
                            <div style={{
                                marginBottom: 24,
                                padding: 16,
                                backgroundColor: 'var(--success-bg, #d4edda)',
                                borderRadius: 8,
                                border: '1px solid var(--success-border, #c3e6cb)'
                            }}>
                                <div style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    marginBottom: 12
                                }}>
                                    <h3 style={{
                                        fontSize: 15,
                                        margin: 0,
                                        color: 'var(--success-dark, #155724)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 8
                                    }}>
                                        <span>✓</span>
                                        Registered {isDoublesFormat ? 'Teams' : 'Players'} ({registeredPlayersNotInList.length})
                                    </h3>
                                    <button
                                        onClick={() => {
                                            // Add all registered players to the list
                                            const newNames = registeredPlayersNotInList.map(reg => {
                                                if (isDoublesFormat && reg.partnerName) {
                                                    return `${reg.displayName} & ${reg.partnerName}`;
                                                }
                                                return reg.displayName;
                                            });

                                            const updatedPlayers = [...currentPlayers, ...newNames];
                                            const updatedText = updatedPlayers.join('\n');
                                            handlePlayersChange({ target: { value: updatedText } });
                                        }}
                                        style={{
                                            padding: '6px 12px',
                                            backgroundColor: 'var(--success)',
                                            color: '#fff',
                                            border: 'none',
                                            borderRadius: 6,
                                            fontSize: 12,
                                            fontWeight: 'bold',
                                            cursor: 'pointer',
                                            transition: 'opacity 0.2s'
                                        }}
                                        onMouseEnter={(e) => e.target.style.opacity = '0.9'}
                                        onMouseLeave={(e) => e.target.style.opacity = '1'}
                                    >
                                        Add All
                                    </button>
                                </div>
                                <div style={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    gap: 8
                                }}>
                                    {registeredPlayersNotInList.map((reg, index) => {
                                        const playerName = isDoublesFormat && reg.partnerName
                                            ? `${reg.displayName} & ${reg.partnerName}`
                                            : reg.displayName;

                                        return (
                                            <button
                                                key={reg.userId || index}
                                                onClick={() => {
                                                    // Add this single player to the list
                                                    const updatedPlayers = [...currentPlayers, playerName];
                                                    const updatedText = updatedPlayers.join('\n');
                                                    handlePlayersChange({ target: { value: updatedText } });
                                                }}
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 6,
                                                    padding: '6px 12px',
                                                    backgroundColor: 'var(--bg-secondary)',
                                                    border: '1px solid var(--border-color)',
                                                    borderRadius: 20,
                                                    fontSize: 13,
                                                    cursor: 'pointer',
                                                    transition: 'all 0.2s'
                                                }}
                                                onMouseEnter={(e) => {
                                                    e.target.style.backgroundColor = 'var(--accent-primary)';
                                                    e.target.style.color = '#fff';
                                                    e.target.style.borderColor = 'var(--accent-primary)';
                                                }}
                                                onMouseLeave={(e) => {
                                                    e.target.style.backgroundColor = 'var(--bg-secondary)';
                                                    e.target.style.color = 'inherit';
                                                    e.target.style.borderColor = 'var(--border-color)';
                                                }}
                                                title={`Click to add ${playerName} to the player list`}
                                            >
                                                <span style={{ color: 'var(--success)', fontWeight: 'bold' }}>+</span>
                                                {playerName}
                                            </button>
                                        );
                                    })}
                                </div>
                                <div style={{
                                    marginTop: 10,
                                    fontSize: 12,
                                    color: 'var(--success-dark, #155724)',
                                    opacity: 0.8
                                }}>
                                    Click a name to add them individually, or use "Add All" to add everyone
                                </div>
                            </div>
                        )}

                        {/* Add Players Section */}
                        <div style={{ marginBottom: 30 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
                                <h3 style={{ fontSize: 16, margin: 0, color: 'var(--text-primary)' }}>{isDoublesFormat ? 'Add Teams' : 'Add Players'}</h3>
                                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                    {/* Hide bulk add when self-scoring is enabled (phone numbers required) */}
                                    {!hasBracket && !isDoublesFormat && !selfScoringEnabled && (
                                        <button
                                            onClick={() => setShowBulkSection(!showBulkSection)}
                                            style={{
                                                padding: '8px 16px',
                                                backgroundColor: showBulkSection ? 'var(--success)' : 'var(--btn-secondary)',
                                                color: '#fff',
                                                border: 'none',
                                                borderRadius: 6,
                                                fontSize: 13,
                                                fontWeight: '500',
                                                cursor: 'pointer',
                                                transition: 'background-color 0.2s'
                                            }}
                                            title="Bulk add multiple players at once"
                                        >
                                            📝 {showBulkSection ? 'Hide Bulk Add' : 'Bulk Add'}
                                        </button>
                                    )}
                                    {currentPlayers.length > 0 && !hasBracket && (
                                        <button
                                            onClick={handleSortAlphabetically}
                                            style={{
                                                padding: '8px 16px',
                                                backgroundColor: 'var(--accent-primary)',
                                                color: '#fff',
                                                border: 'none',
                                                borderRadius: 6,
                                                fontSize: 13,
                                                fontWeight: '500',
                                                cursor: 'pointer',
                                                transition: 'background-color 0.2s'
                                            }}
                                            onMouseEnter={(e) => {
                                                e.target.style.backgroundColor = 'var(--accent-secondary)';
                                            }}
                                            onMouseLeave={(e) => {
                                                e.target.style.backgroundColor = 'var(--accent-primary)';
                                            }}
                                            title="Sort players A-Z"
                                        >
                                            🔤 Sort A-Z
                                        </button>
                                    )}
                                </div>
                            </div>

                            {/* Bulk Entry - Only for singles format */}
                            {showBulkSection && !hasBracket && !isDoublesFormat && (
                                <div style={{
                                    marginBottom: 20,
                                    padding: 16,
                                    backgroundColor: 'var(--bg-tertiary)',
                                    borderRadius: 8,
                                    border: '1px solid var(--border-color)'
                                }}>
                                    <label style={{
                                        display: 'block',
                                        fontSize: 13,
                                        fontWeight: '500',
                                        marginBottom: 8,
                                        color: '#555'
                                    }}>
                                        Enter player names (one per line)
                                    </label>
                                    <textarea
                                        value={playersText}
                                        onChange={handlePlayersChange}
                                        onBlur={handleTextareaBlur}
                                        rows={8}
                                        placeholder="Enter one player per line&#10;John Doe&#10;Jane Smith&#10;..."
                                        style={{
                                            width: '100%',
                                            padding: '12px 16px',
                                            fontSize: 14,
                                            borderRadius: 6,
                                            border: '1px solid var(--border-color)',
                                            backgroundColor: 'var(--bg-secondary)',
                                            resize: 'vertical',
                                            fontFamily: 'inherit',
                                            boxSizing: 'border-box'
                                        }}
                                    />
                                    <div style={{
                                        marginTop: 8,
                                        fontSize: 12,
                                        color: 'var(--text-secondary)'
                                    }}>
                                        Tip: Paste a list of names from a spreadsheet or text file
                                    </div>
                                    {tournament?.isPublic && (
                                        <div style={{
                                            marginTop: 10,
                                            padding: '8px 12px',
                                            backgroundColor: '#fff3cd',
                                            border: '1px solid #ffc107',
                                            borderRadius: 4,
                                            fontSize: 12,
                                            color: '#856404'
                                        }}>
                                            <strong>Note:</strong> Bulk-added players won't be linked to registered accounts. Use "Add Player" below to search for and link registered players.
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Player List Section */}
                        {currentPlayers.length > 0 && (
                            <div>
                                <h3 style={{ fontSize: 16, marginBottom: 16, color: 'var(--text-primary)' }}>
                                    {isDoublesFormat ? 'Team List' : 'Player List'} ({currentPlayers.length})
                                </h3>

                                <div style={{
                                    backgroundColor: 'var(--bg-tertiary)',
                                    borderRadius: 8,
                                    border: '1px solid var(--border-color)',
                                    padding: '16px'
                                }}>
                                    {/* Header Row */}
                                    <div style={{
                                        display: 'grid',
                                        gridTemplateColumns: tournament?.entryFee > 0 ? '1fr 80px 80px 60px' : '1fr 80px 60px',
                                        gap: 8,
                                        padding: '8px 12px',
                                        borderBottom: '2px solid var(--border-color)',
                                        marginBottom: 8,
                                        fontWeight: 'bold',
                                        fontSize: 13,
                                        color: 'var(--text-primary)'
                                    }}>
                                        <div>{isDoublesFormat ? 'Team Name' : 'Player Name'}</div>
                                        {tournament?.entryFee > 0 && <div style={{ textAlign: 'center' }}>Paid</div>}
                                        <div style={{ textAlign: 'center' }}>Edit</div>
                                        <div style={{ textAlign: 'center' }}>Remove</div>
                                    </div>

                                    {/* Player Rows */}
                                    {currentPlayers.map((playerName, index) => (
                                        <div key={index} style={{
                                            display: 'grid',
                                            gridTemplateColumns: tournament?.entryFee > 0 ? '1fr 80px 80px 60px' : '1fr 80px 60px',
                                            gap: 8,
                                            padding: '10px 12px',
                                            backgroundColor: 'var(--bg-secondary)',
                                            borderRadius: 6,
                                            marginBottom: 6,
                                            alignItems: 'center',
                                            border: '1px solid var(--border-color)'
                                        }}>
                                            {editingPlayer === playerName ? (
                                                <>
                                                    <div style={{ gridColumn: tournament?.entryFee > 0 ? 'span 4' : 'span 3' }}>
                                                        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                                                            {isDoublesFormat ? (
                                                                // Doubles edit: two player inputs
                                                                <>
                                                                    <input
                                                                        type="text"
                                                                        value={editPlayer1Value}
                                                                        onChange={(e) => setEditPlayer1Value(e.target.value)}
                                                                        onKeyDown={(e) => handleKeyPress(e, playerName, index)}
                                                                        placeholder="Player 1"
                                                                        style={{
                                                                            flex: 1,
                                                                            minWidth: 100,
                                                                            padding: '6px 10px',
                                                                            fontSize: 14,
                                                                            borderRadius: 4,
                                                                            border: editError ? '1px solid var(--danger)' : '1px solid var(--btn-primary)'
                                                                        }}
                                                                        autoFocus
                                                                    />
                                                                    <span style={{ color: 'var(--text-secondary)', fontWeight: 500 }}>&</span>
                                                                    <input
                                                                        type="text"
                                                                        value={editPlayer2Value}
                                                                        onChange={(e) => setEditPlayer2Value(e.target.value)}
                                                                        onKeyDown={(e) => handleKeyPress(e, playerName, index)}
                                                                        placeholder="Player 2"
                                                                        style={{
                                                                            flex: 1,
                                                                            minWidth: 100,
                                                                            padding: '6px 10px',
                                                                            fontSize: 14,
                                                                            borderRadius: 4,
                                                                            border: editError ? '1px solid var(--danger)' : '1px solid var(--btn-primary)'
                                                                        }}
                                                                    />
                                                                </>
                                                            ) : (
                                                                // Singles edit: single input
                                                                <input
                                                                    type="text"
                                                                    value={editValue}
                                                                    onChange={(e) => setEditValue(e.target.value)}
                                                                    onKeyDown={(e) => handleKeyPress(e, playerName, index)}
                                                                    placeholder="Player name"
                                                                    style={{
                                                                        flex: 1,
                                                                        minWidth: 120,
                                                                        padding: '6px 10px',
                                                                        fontSize: 14,
                                                                        borderRadius: 4,
                                                                        border: editError ? '1px solid var(--danger)' : '1px solid var(--btn-primary)'
                                                                    }}
                                                                    autoFocus
                                                                />
                                                            )}
                                                            {selfScoringEnabled && (
                                                                <input
                                                                    type="tel"
                                                                    value={editPhoneValue}
                                                                    onChange={(e) => {
                                                                        const formatted = formatPhoneInput(e.target.value, editPhoneValue);
                                                                        setEditPhoneValue(formatted);
                                                                    }}
                                                                    onKeyDown={(e) => handleKeyPress(e, playerName, index)}
                                                                    placeholder="(555) 123-4567"
                                                                    style={{
                                                                        width: 130,
                                                                        padding: '6px 10px',
                                                                        fontSize: 14,
                                                                        borderRadius: 4,
                                                                        border: '1px solid var(--border-color)'
                                                                    }}
                                                                />
                                                            )}
                                                            <button
                                                                onClick={() => submitEdit(playerName, index)}
                                                                style={{
                                                                    padding: '6px 12px',
                                                                    backgroundColor: 'var(--success)',
                                                                    color: '#fff',
                                                                    border: 'none',
                                                                    borderRadius: 4,
                                                                    cursor: 'pointer',
                                                                    fontSize: 12
                                                                }}
                                                            >
                                                                ✓
                                                            </button>
                                                            <button
                                                                onClick={cancelEditing}
                                                                style={{
                                                                    padding: '6px 12px',
                                                                    backgroundColor: 'var(--danger)',
                                                                    color: '#fff',
                                                                    border: 'none',
                                                                    borderRadius: 4,
                                                                    cursor: 'pointer',
                                                                    fontSize: 12
                                                                }}
                                                            >
                                                                ✗
                                                            </button>
                                                        </div>
                                                        {editError && (
                                                            <div style={{ fontSize: 12, color: 'var(--danger)', marginTop: 4 }}>
                                                                {editError}
                                                            </div>
                                                        )}
                                                    </div>
                                                </>
                                            ) : (
                                                <>
                                                    <div style={{ fontSize: 14, color: 'var(--text-primary)' }}>
                                                        {/* Show player name and phone (if self-scoring enabled) */}
                                                        <div>{playerName}</div>
                                                        {selfScoringEnabled && playerPhones[playerName]?.phoneMasked && (
                                                            <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
                                                                {playerPhones[playerName].phoneMasked}
                                                            </div>
                                                        )}
                                                    </div>
                                                    {tournament?.entryFee > 0 && (
                                                        <div style={{ display: 'flex', justifyContent: 'center' }}>
                                                            <input
                                                                type="checkbox"
                                                                checked={playerPayments[playerName]?.paid || false}
                                                                onChange={() => handlePaymentToggle(playerName)}
                                                                style={{
                                                                    cursor: 'pointer',
                                                                    width: 18,
                                                                    height: 18
                                                                }}
                                                                title={playerPayments[playerName]?.paid ? "Mark as unpaid" : "Mark as paid"}
                                                            />
                                                        </div>
                                                    )}
                                                    <div style={{ display: 'flex', justifyContent: 'center' }}>
                                                        <button
                                                            onClick={() => startEditing(playerName)}
                                                            style={{
                                                                background: 'none',
                                                                border: 'none',
                                                                color: 'var(--accent-primary)',
                                                                cursor: 'pointer',
                                                                fontSize: 16,
                                                                padding: '4px 8px'
                                                            }}
                                                            title="Edit player name"
                                                        >
                                                            ✏️
                                                        </button>
                                                    </div>
                                                    <div style={{ display: 'flex', justifyContent: 'center' }}>
                                                        <button
                                                            type="button"
                                                            onClick={(e) => {
                                                                e.preventDefault();
                                                                e.stopPropagation();
                                                                handleRemovePlayer(playerName);
                                                            }}
                                                            disabled={hasBracket}
                                                            style={{
                                                                background: 'none',
                                                                border: 'none',
                                                                color: hasBracket ? '#bbb' : '#c62828',
                                                                cursor: hasBracket ? 'not-allowed' : 'pointer',
                                                                fontSize: 16,
                                                                padding: '4px 8px',
                                                                opacity: hasBracket ? 0.5 : 1,
                                                                transition: 'all 0.15s ease'
                                                            }}
                                                            title={hasBracket ? "Cannot remove after tournament starts" : "Remove player"}
                                                        >
                                                            🗑️
                                                        </button>
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    ))}

                                    {/* Add New Player/Team Row */}
                                    {addingNewPlayer ? (
                                        isDoublesFormat ? (
                                            /* Doubles/Scotch Doubles Entry Form */
                                            <div ref={addPlayerFormRef} style={{
                                                padding: '16px',
                                                backgroundColor: 'var(--bg-secondary)',
                                                borderRadius: 8,
                                                border: '2px solid var(--btn-primary)',
                                                marginTop: 8
                                            }}>
                                                <div style={{
                                                    fontSize: 13,
                                                    fontWeight: '600',
                                                    color: 'var(--text-primary)',
                                                    marginBottom: 12,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 6
                                                }}>
                                                    <span>Add Team</span>
                                                    <span style={{
                                                        fontSize: 11,
                                                        color: 'var(--text-secondary)',
                                                        fontWeight: 'normal'
                                                    }}>
                                                        ({tournament?.playerFormat})
                                                    </span>
                                                </div>
                                                <div style={{
                                                    display: 'flex',
                                                    gap: 12,
                                                    alignItems: 'flex-start',
                                                    flexWrap: 'wrap'
                                                }}>
                                                    {/* Player 1 Input */}
                                                    <div style={{ flex: 1, minWidth: 140, position: 'relative' }}>
                                                        <label style={{
                                                            fontSize: 11,
                                                            color: 'var(--text-secondary)',
                                                            display: 'block',
                                                            marginBottom: 4
                                                        }}>
                                                            Player 1
                                                        </label>
                                                        <input
                                                            type="text"
                                                            value={newPlayer1Value}
                                                            onChange={(e) => {
                                                                setNewPlayer1Value(e.target.value);
                                                                setSelectedPlayer1UserId(null); // Clear user ID when typing manually
                                                                setShowPlayer1Dropdown(true);
                                                            }}
                                                            onFocus={() => setShowPlayer1Dropdown(true)}
                                                            onBlur={() => setTimeout(() => setShowPlayer1Dropdown(false), 200)}
                                                            placeholder={tournament?.isPublic ? "Name or search..." : "Enter name..."}
                                                            style={{
                                                                width: '100%',
                                                                padding: '8px 10px',
                                                                fontSize: 14,
                                                                borderRadius: 4,
                                                                border: '1px solid var(--border-color)',
                                                                boxSizing: 'border-box'
                                                            }}
                                                            autoFocus
                                                        />
                                                        {/* Player 1 Autocomplete Dropdown */}
                                                        {showPlayer1Dropdown && tournament?.isPublic && (player1SearchResults.length > 0 || isSearchingPlayer1) && (
                                                            <div style={{
                                                                position: 'absolute',
                                                                top: '100%',
                                                                left: 0,
                                                                right: 0,
                                                                backgroundColor: 'var(--bg-secondary)',
                                                                border: '1px solid var(--border-color)',
                                                                borderRadius: 4,
                                                                boxShadow: 'var(--shadow-md)',
                                                                zIndex: 100,
                                                                maxHeight: 150,
                                                                overflowY: 'auto'
                                                            }}>
                                                                {isSearchingPlayer1 ? (
                                                                    <div style={{ padding: 10, textAlign: 'center', color: 'var(--text-secondary)', fontSize: 12 }}>
                                                                        Searching...
                                                                    </div>
                                                                ) : (
                                                                    player1SearchResults.map(player => (
                                                                        <div
                                                                            key={player.id}
                                                                            style={{
                                                                                padding: '8px 10px',
                                                                                cursor: 'pointer',
                                                                                fontSize: 13,
                                                                                borderBottom: '1px solid #eee',
                                                                                display: 'flex',
                                                                                alignItems: 'center',
                                                                                gap: 8
                                                                            }}
                                                                            onMouseDown={() => selectPlayer1(player)}
                                                                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--bg-tertiary)'}
                                                                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'var(--bg-secondary)'}
                                                                        >
                                                                            {player.photoURL ? (
                                                                                <img
                                                                                    src={player.photoURL}
                                                                                    alt=""
                                                                                    style={{ width: 20, height: 20, borderRadius: '50%' }}
                                                                                />
                                                                            ) : (
                                                                                <div style={{
                                                                                    width: 20,
                                                                                    height: 20,
                                                                                    borderRadius: '50%',
                                                                                    backgroundColor: 'var(--accent-primary)',
                                                                                    color: '#fff',
                                                                                    display: 'flex',
                                                                                    alignItems: 'center',
                                                                                    justifyContent: 'center',
                                                                                    fontSize: 10,
                                                                                    fontWeight: 'bold'
                                                                                }}>
                                                                                    {player.displayName?.charAt(0).toUpperCase()}
                                                                                </div>
                                                                            )}
                                                                            <span>{player.displayName}</span>
                                                                            {player.playerId && (
                                                                                <span style={{
                                                                                    fontSize: 10,
                                                                                    color: '#888',
                                                                                    backgroundColor: '#f0f0f0',
                                                                                    padding: '1px 4px',
                                                                                    borderRadius: 2
                                                                                }}>
                                                                                    #{player.playerId}
                                                                                </span>
                                                                            )}
                                                                        </div>
                                                                    ))
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>

                                                    {/* & Symbol */}
                                                    <div style={{
                                                        fontSize: 18,
                                                        fontWeight: 'bold',
                                                        color: 'var(--accent-primary)',
                                                        paddingTop: 28
                                                    }}>
                                                        &
                                                    </div>

                                                    {/* Player 2 Input */}
                                                    <div style={{ flex: 1, minWidth: 140, position: 'relative' }}>
                                                        <label style={{
                                                            fontSize: 11,
                                                            color: 'var(--text-secondary)',
                                                            display: 'block',
                                                            marginBottom: 4
                                                        }}>
                                                            Player 2
                                                        </label>
                                                        <input
                                                            type="text"
                                                            value={newPlayer2Value}
                                                            onChange={(e) => {
                                                                setNewPlayer2Value(e.target.value);
                                                                setSelectedPlayer2UserId(null); // Clear user ID when typing manually
                                                                setShowPlayer2Dropdown(true);
                                                            }}
                                                            onFocus={() => setShowPlayer2Dropdown(true)}
                                                            onBlur={() => setTimeout(() => setShowPlayer2Dropdown(false), 200)}
                                                            placeholder={tournament?.isPublic ? "Name or search..." : "Enter name..."}
                                                            style={{
                                                                width: '100%',
                                                                padding: '8px 10px',
                                                                fontSize: 14,
                                                                borderRadius: 4,
                                                                border: '1px solid var(--border-color)',
                                                                boxSizing: 'border-box'
                                                            }}
                                                        />
                                                        {/* Player 2 Autocomplete Dropdown */}
                                                        {showPlayer2Dropdown && tournament?.isPublic && (player2SearchResults.length > 0 || isSearchingPlayer2) && (
                                                            <div style={{
                                                                position: 'absolute',
                                                                top: '100%',
                                                                left: 0,
                                                                right: 0,
                                                                backgroundColor: 'var(--bg-secondary)',
                                                                border: '1px solid var(--border-color)',
                                                                borderRadius: 4,
                                                                boxShadow: 'var(--shadow-md)',
                                                                zIndex: 100,
                                                                maxHeight: 150,
                                                                overflowY: 'auto'
                                                            }}>
                                                                {isSearchingPlayer2 ? (
                                                                    <div style={{ padding: 10, textAlign: 'center', color: 'var(--text-secondary)', fontSize: 12 }}>
                                                                        Searching...
                                                                    </div>
                                                                ) : (
                                                                    player2SearchResults.map(player => (
                                                                        <div
                                                                            key={player.id}
                                                                            style={{
                                                                                padding: '8px 10px',
                                                                                cursor: 'pointer',
                                                                                fontSize: 13,
                                                                                borderBottom: '1px solid #eee',
                                                                                display: 'flex',
                                                                                alignItems: 'center',
                                                                                gap: 8
                                                                            }}
                                                                            onMouseDown={() => selectPlayer2(player)}
                                                                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--bg-tertiary)'}
                                                                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'var(--bg-secondary)'}
                                                                        >
                                                                            {player.photoURL ? (
                                                                                <img
                                                                                    src={player.photoURL}
                                                                                    alt=""
                                                                                    style={{ width: 20, height: 20, borderRadius: '50%' }}
                                                                                />
                                                                            ) : (
                                                                                <div style={{
                                                                                    width: 20,
                                                                                    height: 20,
                                                                                    borderRadius: '50%',
                                                                                    backgroundColor: 'var(--accent-primary)',
                                                                                    color: '#fff',
                                                                                    display: 'flex',
                                                                                    alignItems: 'center',
                                                                                    justifyContent: 'center',
                                                                                    fontSize: 10,
                                                                                    fontWeight: 'bold'
                                                                                }}>
                                                                                    {player.displayName?.charAt(0).toUpperCase()}
                                                                                </div>
                                                                            )}
                                                                            <span>{player.displayName}</span>
                                                                            {player.playerId && (
                                                                                <span style={{
                                                                                    fontSize: 10,
                                                                                    color: 'var(--text-muted)',
                                                                                    backgroundColor: 'var(--bg-tertiary)',
                                                                                    padding: '1px 4px',
                                                                                    borderRadius: 2
                                                                                }}>
                                                                                    #{player.playerId}
                                                                                </span>
                                                                            )}
                                                                        </div>
                                                                    ))
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>

                                                {/* Team Phone Number and Buttons Row - when self-scoring is enabled */}
                                                {selfScoringEnabled ? (
                                                    <div style={{ marginTop: 12 }}>
                                                        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
                                                            <div style={{ flex: 1 }}>
                                                                <label style={{ fontSize: 12, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>
                                                                    Team Phone Number <span style={{ color: 'var(--text-muted)', fontWeight: 'normal' }}>(optional)</span>
                                                                </label>
                                                                <input
                                                                    type="tel"
                                                                    value={teamPhone}
                                                                    onChange={(e) => {
                                                                        const formatted = formatPhoneInput(e.target.value, teamPhone);
                                                                        setTeamPhone(formatted);
                                                                        setTeamPhoneError('');
                                                                    }}
                                                                    onKeyDown={(e) => {
                                                                        if (e.key === 'Enter') {
                                                                            e.preventDefault();
                                                                            submitDoublesTeam();
                                                                        } else if (e.key === 'Escape') {
                                                                            cancelAddingDoublesTeam();
                                                                        }
                                                                    }}
                                                                    placeholder="(555) 123-4567"
                                                                    style={{
                                                                        width: '100%',
                                                                        padding: '8px 12px',
                                                                        fontSize: 14,
                                                                        borderRadius: 4,
                                                                        border: teamPhoneError ? '1px solid var(--danger)' : '1px solid var(--border-color)',
                                                                        boxSizing: 'border-box',
                                                                        backgroundColor: 'var(--bg-input)',
                                                                        color: 'var(--text-primary)'
                                                                    }}
                                                                />
                                                            </div>
                                                            <button
                                                                onClick={submitDoublesTeam}
                                                                style={{
                                                                    padding: '8px 14px',
                                                                    backgroundColor: 'var(--success)',
                                                                    color: '#fff',
                                                                    border: 'none',
                                                                    borderRadius: 4,
                                                                    cursor: 'pointer',
                                                                    fontSize: 13,
                                                                    fontWeight: '500',
                                                                    whiteSpace: 'nowrap'
                                                                }}
                                                            >
                                                                Add Team
                                                            </button>
                                                            <button
                                                                onClick={cancelAddingDoublesTeam}
                                                                style={{
                                                                    padding: '8px 12px',
                                                                    backgroundColor: 'var(--danger)',
                                                                    color: '#fff',
                                                                    border: 'none',
                                                                    borderRadius: 4,
                                                                    cursor: 'pointer',
                                                                    fontSize: 13
                                                                }}
                                                            >
                                                                Cancel
                                                            </button>
                                                        </div>
                                                        {teamPhoneError && (
                                                            <div style={{ fontSize: 12, color: 'var(--danger)', marginTop: 4 }}>
                                                                {teamPhoneError}
                                                            </div>
                                                        )}
                                                        <div style={{ fontSize: 10, color: 'var(--text-secondary)', marginTop: 4 }}>
                                                            By adding, team consents to receive SMS notifications. Message and data rates may apply.
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <div style={{
                                                        display: 'flex',
                                                        gap: 6,
                                                        marginTop: 12
                                                    }}>
                                                        <button
                                                            onClick={submitDoublesTeam}
                                                            style={{
                                                                padding: '8px 14px',
                                                                backgroundColor: 'var(--success)',
                                                                color: '#fff',
                                                                border: 'none',
                                                                borderRadius: 4,
                                                                cursor: 'pointer',
                                                                fontSize: 13,
                                                                fontWeight: '500'
                                                            }}
                                                        >
                                                            Add Team
                                                        </button>
                                                        <button
                                                            onClick={cancelAddingDoublesTeam}
                                                            style={{
                                                                padding: '8px 12px',
                                                                backgroundColor: 'var(--danger)',
                                                                color: '#fff',
                                                                border: 'none',
                                                                borderRadius: 4,
                                                                cursor: 'pointer',
                                                                fontSize: 13
                                                            }}
                                                        >
                                                            Cancel
                                                        </button>
                                                    </div>
                                                )}
                                                {newPlayerError && (
                                                    <div style={{ fontSize: 12, color: 'var(--danger)', marginTop: 8 }}>
                                                        {newPlayerError}
                                                    </div>
                                                )}
                                                {tournament?.isPublic && !selfScoringEnabled && (
                                                    <div style={{
                                                        fontSize: 11,
                                                        color: 'var(--text-secondary)',
                                                        marginTop: 10,
                                                        fontStyle: 'italic'
                                                    }}>
                                                        Tip: Start typing to search for registered players
                                                    </div>
                                                )}
                                            </div>
                                        ) : (
                                            /* Singles Entry Form */
                                            <div ref={addPlayerFormRef} style={{
                                                padding: '12px',
                                                backgroundColor: 'var(--bg-secondary)',
                                                borderRadius: 6,
                                                marginTop: 8,
                                                border: '2px solid var(--btn-primary)'
                                            }}>
                                                <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', flexWrap: selfScoringEnabled ? 'wrap' : 'nowrap' }}>
                                                    <div style={{ flex: selfScoringEnabled ? '1 1 100%' : 1, position: 'relative' }}>
                                                        {selfScoringEnabled && (
                                                            <label style={{ fontSize: 11, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>
                                                                Player Name
                                                            </label>
                                                        )}
                                                        <input
                                                            type="text"
                                                            value={newPlayerValue}
                                                            onChange={(e) => {
                                                                setNewPlayerValue(e.target.value);
                                                                setShowSinglesDropdown(true);
                                                                setNewPlayerError('');
                                                                setSelectedSinglesUserId(null); // Clear userId when manually typing
                                                            }}
                                                            onFocus={() => setShowSinglesDropdown(true)}
                                                            onBlur={() => setTimeout(() => setShowSinglesDropdown(false), 200)}
                                                            onKeyDown={!selfScoringEnabled ? handleNewPlayerKeyPress : undefined}
                                                            placeholder={tournament?.isPublic ? "Name or search registered players..." : "Enter player name..."}
                                                            style={{
                                                                width: '100%',
                                                                padding: '8px 10px',
                                                                fontSize: 14,
                                                                borderRadius: 4,
                                                                border: newPlayerError ? '1px solid var(--danger)' : '1px solid var(--border-color)',
                                                                boxSizing: 'border-box',
                                                                backgroundColor: 'var(--bg-input)',
                                                                color: 'var(--text-primary)'
                                                            }}
                                                            autoFocus
                                                        />
                                                        {/* Singles Autocomplete Dropdown */}
                                                        {showSinglesDropdown && tournament?.isPublic && (singlesSearchResults.length > 0 || isSearchingSingles) && (
                                                            <div style={{
                                                                position: 'absolute',
                                                                top: '100%',
                                                                left: 0,
                                                                right: 0,
                                                                backgroundColor: 'var(--bg-secondary)',
                                                                border: '1px solid var(--border-color)',
                                                                borderRadius: 4,
                                                                boxShadow: 'var(--shadow-md)',
                                                                zIndex: 100,
                                                                maxHeight: 180,
                                                                overflowY: 'auto'
                                                            }}>
                                                                {isSearchingSingles ? (
                                                                    <div style={{ padding: 10, textAlign: 'center', color: 'var(--text-secondary)', fontSize: 12 }}>
                                                                        Searching...
                                                                    </div>
                                                                ) : (
                                                                    singlesSearchResults.map(player => (
                                                                        <div
                                                                            key={player.id}
                                                                            style={{
                                                                                padding: '8px 10px',
                                                                                cursor: 'pointer',
                                                                                fontSize: 13,
                                                                                borderBottom: '1px solid #eee',
                                                                                display: 'flex',
                                                                                alignItems: 'center',
                                                                                gap: 8
                                                                            }}
                                                                            onMouseDown={() => selectSinglesPlayer(player)}
                                                                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--bg-tertiary)'}
                                                                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'var(--bg-secondary)'}
                                                                        >
                                                                            {player.photoURL ? (
                                                                                <img
                                                                                    src={player.photoURL}
                                                                                    alt=""
                                                                                    style={{ width: 24, height: 24, borderRadius: '50%' }}
                                                                                />
                                                                            ) : (
                                                                                <div style={{
                                                                                    width: 24,
                                                                                    height: 24,
                                                                                    borderRadius: '50%',
                                                                                    backgroundColor: 'var(--accent-primary)',
                                                                                    color: '#fff',
                                                                                    display: 'flex',
                                                                                    alignItems: 'center',
                                                                                    justifyContent: 'center',
                                                                                    fontSize: 11,
                                                                                    fontWeight: 'bold'
                                                                                }}>
                                                                                    {player.displayName?.charAt(0).toUpperCase()}
                                                                                </div>
                                                                            )}
                                                                            <span style={{ flex: 1 }}>{player.displayName}</span>
                                                                            {player.playerId && (
                                                                                <span style={{
                                                                                    fontSize: 11,
                                                                                    color: 'var(--text-muted)',
                                                                                    backgroundColor: 'var(--bg-tertiary)',
                                                                                    padding: '2px 6px',
                                                                                    borderRadius: 3,
                                                                                    fontFamily: 'monospace'
                                                                                }}>
                                                                                    #{player.playerId}
                                                                                </span>
                                                                            )}
                                                                        </div>
                                                                    ))
                                                                )}
                                                            </div>
                                                        )}
                                                        {newPlayerError && (
                                                            <div style={{ fontSize: 12, color: 'var(--danger)', marginTop: 4 }}>
                                                                {newPlayerError}
                                                            </div>
                                                        )}
                                                    </div>

                                                    {/* Phone Number Input - Only when self-scoring is enabled */}
                                                    {selfScoringEnabled && (
                                                        <div style={{ flex: '1 1 100%', marginTop: 8 }}>
                                                            <label style={{ fontSize: 11, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>
                                                                Phone Number <span style={{ color: 'var(--text-muted)', fontWeight: 'normal' }}>(optional)</span>
                                                            </label>
                                                            <input
                                                                type="tel"
                                                                value={newPlayerPhone}
                                                                onChange={(e) => {
                                                                    const formatted = formatPhoneInput(e.target.value, newPlayerPhone);
                                                                    setNewPlayerPhone(formatted);
                                                                    setNewPlayerPhoneError('');
                                                                }}
                                                                onKeyDown={(e) => {
                                                                    if (e.key === 'Enter') {
                                                                        e.preventDefault();
                                                                        submitNewPlayer();
                                                                    } else if (e.key === 'Escape') {
                                                                        cancelAddingPlayer();
                                                                    }
                                                                }}
                                                                placeholder="(555) 123-4567"
                                                                style={{
                                                                    width: '100%',
                                                                    padding: '8px 10px',
                                                                    fontSize: 14,
                                                                    borderRadius: 4,
                                                                    border: newPlayerPhoneError ? '1px solid var(--danger)' : '1px solid var(--border-color)',
                                                                    boxSizing: 'border-box',
                                                                    backgroundColor: 'var(--bg-input)',
                                                                    color: 'var(--text-primary)'
                                                                }}
                                                            />
                                                            {newPlayerPhoneError && (
                                                                <div style={{ fontSize: 12, color: 'var(--danger)', marginTop: 4 }}>
                                                                    {newPlayerPhoneError}
                                                                </div>
                                                            )}
                                                            <div style={{ fontSize: 10, color: 'var(--text-secondary)', marginTop: 4 }}>
                                                                By adding, player consents to receive SMS notifications. Message and data rates may apply.
                                                            </div>
                                                        </div>
                                                    )}

                                                    {/* Action Buttons */}
                                                    <div style={{
                                                        display: 'flex',
                                                        gap: 8,
                                                        marginTop: selfScoringEnabled ? 12 : 0,
                                                        flex: selfScoringEnabled ? '1 1 100%' : 'none',
                                                        justifyContent: selfScoringEnabled ? 'flex-end' : 'flex-start'
                                                    }}>
                                                        <button
                                                            onClick={submitNewPlayer}
                                                            style={{
                                                                padding: '8px 14px',
                                                                backgroundColor: 'var(--success)',
                                                                color: '#fff',
                                                                border: 'none',
                                                                borderRadius: 4,
                                                                cursor: 'pointer',
                                                                fontSize: 13,
                                                                fontWeight: '500'
                                                            }}
                                                            title="Add player"
                                                        >
                                                            Add
                                                        </button>
                                                        <button
                                                            onClick={cancelAddingPlayer}
                                                            style={{
                                                                padding: '8px 12px',
                                                                backgroundColor: 'var(--danger)',
                                                                color: '#fff',
                                                                border: 'none',
                                                                borderRadius: 4,
                                                                cursor: 'pointer',
                                                                fontSize: 13
                                                            }}
                                                            title="Cancel"
                                                        >
                                                            Cancel
                                                        </button>
                                                    </div>
                                                </div>
                                                {!selfScoringEnabled && newPlayerError && (
                                                    <div style={{ fontSize: 12, color: 'var(--danger)', marginTop: 6 }}>
                                                        {newPlayerError}
                                                    </div>
                                                )}
                                                {tournament?.isPublic && !selfScoringEnabled && (
                                                    <div style={{
                                                        fontSize: 11,
                                                        color: 'var(--text-secondary)',
                                                        marginTop: 8,
                                                        fontStyle: 'italic'
                                                    }}>
                                                        Tip: Start typing to search for registered players
                                                    </div>
                                                )}
                                            </div>
                                        )
                                    ) : (
                                        <button
                                            onClick={startAddingPlayer}
                                            disabled={hasBracket}
                                            style={{
                                                width: '100%',
                                                padding: '10px',
                                                backgroundColor: hasBracket ? 'var(--text-muted)' : 'var(--success)',
                                                color: '#fff',
                                                border: 'none',
                                                borderRadius: 6,
                                                fontSize: 14,
                                                fontWeight: '500',
                                                cursor: hasBracket ? 'not-allowed' : 'pointer',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                gap: 8,
                                                marginTop: 8,
                                                transition: 'background-color 0.2s'
                                            }}
                                            onMouseEnter={(e) => {
                                                if (!hasBracket) e.target.style.backgroundColor = 'var(--success-hover)';
                                            }}
                                            onMouseLeave={(e) => {
                                                if (!hasBracket) e.target.style.backgroundColor = 'var(--success)';
                                            }}
                                            title={hasBracket ? "Cannot add players after tournament starts" : isDoublesFormat ? "Add a new team" : "Add a new player"}
                                        >
                                            <span style={{ fontSize: 18 }}>+</span>
                                            <span>{isDoublesFormat ? 'Add Team' : 'Add Player'}</span>
                                        </button>
                                    )}
                                </div>

                                {/* Payment Summary */}
                                {tournament?.entryFee > 0 && (
                                    <div style={{
                                        marginTop: 16,
                                        padding: '16px',
                                        backgroundColor: 'var(--info-bg)',
                                        borderRadius: 8,
                                        border: '1px solid var(--info-border)'
                                    }}>
                                        <div style={{
                                            fontSize: 15,
                                            fontWeight: 'bold',
                                            color: 'var(--text-primary)',
                                            marginBottom: 8
                                        }}>
                                            Payment Summary
                                        </div>
                                        <div style={{
                                            display: 'grid',
                                            gridTemplateColumns: window.innerWidth > 768 ? '1fr 1fr' : '1fr',
                                            gap: 12
                                        }}>
                                            <div style={{ fontSize: 14, color: 'var(--text-secondary)' }}>
                                                Players Paid: <span style={{ fontWeight: '600', color: 'var(--text-primary)' }}>
                                                    {paidCount} / {totalPlayers}
                                                </span>
                                            </div>
                                            <div style={{ fontSize: 14, color: 'var(--text-secondary)' }}>
                                                Total Collected: <span style={{ fontWeight: '600', color: 'var(--success)' }}>
                                                    ${totalCollected}
                                                </span> / <span style={{ fontWeight: '600', color: 'var(--text-primary)' }}>
                                                    ${totalExpected}
                                                </span>
                                            </div>
                                        </div>
                                        {/* Progress Bar */}
                                        <div style={{
                                            marginTop: 12,
                                            height: 8,
                                            backgroundColor: 'var(--border-color)',
                                            borderRadius: 4,
                                            overflow: 'hidden'
                                        }}>
                                            <div style={{
                                                height: '100%',
                                                width: `${totalPlayers > 0 ? (paidCount / totalPlayers) * 100 : 0}%`,
                                                backgroundColor: 'var(--success)',
                                                transition: 'width 0.3s ease'
                                            }} />
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        {currentPlayers.length === 0 && (
                            <div style={{
                                textAlign: 'center',
                                padding: '40px 20px',
                                color: 'var(--text-muted)',
                                fontSize: 14
                            }}>
                                <p style={{ marginBottom: 16 }}>
                                    {isDoublesFormat ? 'No teams added yet.' : 'No players added yet.'}
                                </p>
                                {!hasBracket && !addingNewPlayer && (
                                    <button
                                        onClick={startAddingPlayer}
                                        style={{
                                            padding: '12px 24px',
                                            backgroundColor: 'var(--success)',
                                            color: '#fff',
                                            border: 'none',
                                            borderRadius: 6,
                                            fontSize: 14,
                                            fontWeight: '500',
                                            cursor: 'pointer',
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: 8,
                                            transition: 'background-color 0.2s'
                                        }}
                                        onMouseEnter={(e) => e.target.style.backgroundColor = 'var(--success-hover)'}
                                        onMouseLeave={(e) => e.target.style.backgroundColor = 'var(--success)'}
                                    >
                                        <span style={{ fontSize: 18 }}>+</span>
                                        <span>{isDoublesFormat ? 'Add First Team' : 'Add First Player'}</span>
                                    </button>
                                )}
                                {addingNewPlayer && (
                                    isDoublesFormat ? (
                                        /* Doubles Empty State Form */
                                        <div ref={addPlayerFormRef} style={{
                                            maxWidth: '500px',
                                            margin: '0 auto',
                                            textAlign: 'left',
                                            padding: '16px',
                                            backgroundColor: 'var(--bg-secondary)',
                                            borderRadius: 8,
                                            border: '2px solid var(--btn-primary)'
                                        }}>
                                            <div style={{
                                                fontSize: 13,
                                                fontWeight: '600',
                                                color: 'var(--text-primary)',
                                                marginBottom: 12
                                            }}>
                                                Add Team ({tournament?.playerFormat})
                                            </div>
                                            <div style={{
                                                display: 'flex',
                                                gap: 12,
                                                alignItems: 'flex-start',
                                                flexWrap: 'wrap'
                                            }}>
                                                {/* Player 1 */}
                                                <div style={{ flex: 1, minWidth: 140, position: 'relative' }}>
                                                    <label style={{ fontSize: 11, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>
                                                        Player 1
                                                    </label>
                                                    <input
                                                        type="text"
                                                        value={newPlayer1Value}
                                                        onChange={(e) => {
                                                            setNewPlayer1Value(e.target.value);
                                                            setSelectedPlayer1UserId(null); // Clear user ID when typing manually
                                                            setShowPlayer1Dropdown(true);
                                                        }}
                                                        onFocus={() => setShowPlayer1Dropdown(true)}
                                                        onBlur={() => setTimeout(() => setShowPlayer1Dropdown(false), 200)}
                                                        placeholder={tournament?.isPublic ? "Name or search..." : "Enter name..."}
                                                        style={{
                                                            width: '100%',
                                                            padding: '8px 10px',
                                                            fontSize: 14,
                                                            borderRadius: 4,
                                                            border: '1px solid var(--border-color)',
                                                            boxSizing: 'border-box'
                                                        }}
                                                        autoFocus
                                                    />
                                                    {showPlayer1Dropdown && tournament?.isPublic && (player1SearchResults.length > 0 || isSearchingPlayer1) && (
                                                        <div style={{
                                                            position: 'absolute',
                                                            top: '100%',
                                                            left: 0,
                                                            right: 0,
                                                            backgroundColor: '#fff',
                                                            border: '1px solid #ddd',
                                                            borderRadius: 4,
                                                            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                                                            zIndex: 100,
                                                            maxHeight: 150,
                                                            overflowY: 'auto'
                                                        }}>
                                                            {isSearchingPlayer1 ? (
                                                                <div style={{ padding: 10, textAlign: 'center', color: 'var(--text-secondary)', fontSize: 12 }}>
                                                                    Searching...
                                                                </div>
                                                            ) : (
                                                                player1SearchResults.map(player => (
                                                                    <div
                                                                        key={player.id}
                                                                        style={{
                                                                            padding: '8px 10px',
                                                                            cursor: 'pointer',
                                                                            fontSize: 13,
                                                                            borderBottom: '1px solid #eee',
                                                                            display: 'flex',
                                                                            alignItems: 'center',
                                                                            gap: 8
                                                                        }}
                                                                        onMouseDown={() => selectPlayer1(player)}
                                                                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--bg-tertiary)'}
                                                                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'var(--bg-secondary)'}
                                                                    >
                                                                        {player.photoURL ? (
                                                                            <img
                                                                                src={player.photoURL}
                                                                                alt=""
                                                                                style={{ width: 20, height: 20, borderRadius: '50%' }}
                                                                            />
                                                                        ) : (
                                                                            <div style={{
                                                                                width: 20,
                                                                                height: 20,
                                                                                borderRadius: '50%',
                                                                                backgroundColor: '#667eea',
                                                                                color: '#fff',
                                                                                display: 'flex',
                                                                                alignItems: 'center',
                                                                                justifyContent: 'center',
                                                                                fontSize: 10,
                                                                                fontWeight: 'bold'
                                                                            }}>
                                                                                {player.displayName?.charAt(0).toUpperCase()}
                                                                            </div>
                                                                        )}
                                                                        <span style={{ flex: 1 }}>{player.displayName}</span>
                                                                        {player.playerId && (
                                                                            <span style={{
                                                                                fontSize: 11,
                                                                                color: 'var(--text-muted)',
                                                                                backgroundColor: 'var(--bg-tertiary)',
                                                                                padding: '2px 6px',
                                                                                borderRadius: 3,
                                                                                fontFamily: 'monospace'
                                                                            }}>
                                                                                #{player.playerId}
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                ))
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                                <div style={{ fontSize: 18, fontWeight: 'bold', color: 'var(--accent-primary)', paddingTop: 28 }}>&</div>
                                                {/* Player 2 */}
                                                <div style={{ flex: 1, minWidth: 140, position: 'relative' }}>
                                                    <label style={{ fontSize: 11, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>
                                                        Player 2
                                                    </label>
                                                    <input
                                                        type="text"
                                                        value={newPlayer2Value}
                                                        onChange={(e) => {
                                                            setNewPlayer2Value(e.target.value);
                                                            setSelectedPlayer2UserId(null); // Clear user ID when typing manually
                                                            setShowPlayer2Dropdown(true);
                                                        }}
                                                        onFocus={() => setShowPlayer2Dropdown(true)}
                                                        onBlur={() => setTimeout(() => setShowPlayer2Dropdown(false), 200)}
                                                        placeholder={tournament?.isPublic ? "Name or search..." : "Enter name..."}
                                                        style={{
                                                            width: '100%',
                                                            padding: '8px 10px',
                                                            fontSize: 14,
                                                            borderRadius: 4,
                                                            border: '1px solid var(--border-color)',
                                                            boxSizing: 'border-box'
                                                        }}
                                                    />
                                                    {showPlayer2Dropdown && tournament?.isPublic && (player2SearchResults.length > 0 || isSearchingPlayer2) && (
                                                        <div style={{
                                                            position: 'absolute',
                                                            top: '100%',
                                                            left: 0,
                                                            right: 0,
                                                            backgroundColor: '#fff',
                                                            border: '1px solid #ddd',
                                                            borderRadius: 4,
                                                            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                                                            zIndex: 100,
                                                            maxHeight: 150,
                                                            overflowY: 'auto'
                                                        }}>
                                                            {isSearchingPlayer2 ? (
                                                                <div style={{ padding: 10, textAlign: 'center', color: 'var(--text-secondary)', fontSize: 12 }}>
                                                                    Searching...
                                                                </div>
                                                            ) : (
                                                                player2SearchResults.map(player => (
                                                                    <div
                                                                        key={player.id}
                                                                        style={{
                                                                            padding: '8px 10px',
                                                                            cursor: 'pointer',
                                                                            fontSize: 13,
                                                                            borderBottom: '1px solid #eee',
                                                                            display: 'flex',
                                                                            alignItems: 'center',
                                                                            gap: 8
                                                                        }}
                                                                        onMouseDown={() => selectPlayer2(player)}
                                                                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--bg-tertiary)'}
                                                                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'var(--bg-secondary)'}
                                                                    >
                                                                        {player.photoURL ? (
                                                                            <img
                                                                                src={player.photoURL}
                                                                                alt=""
                                                                                style={{ width: 20, height: 20, borderRadius: '50%' }}
                                                                            />
                                                                        ) : (
                                                                            <div style={{
                                                                                width: 20,
                                                                                height: 20,
                                                                                borderRadius: '50%',
                                                                                backgroundColor: '#667eea',
                                                                                color: '#fff',
                                                                                display: 'flex',
                                                                                alignItems: 'center',
                                                                                justifyContent: 'center',
                                                                                fontSize: 10,
                                                                                fontWeight: 'bold'
                                                                            }}>
                                                                                {player.displayName?.charAt(0).toUpperCase()}
                                                                            </div>
                                                                        )}
                                                                        <span style={{ flex: 1 }}>{player.displayName}</span>
                                                                        {player.playerId && (
                                                                            <span style={{
                                                                                fontSize: 11,
                                                                                color: 'var(--text-muted)',
                                                                                backgroundColor: 'var(--bg-tertiary)',
                                                                                padding: '2px 6px',
                                                                                borderRadius: 3,
                                                                                fontFamily: 'monospace'
                                                                            }}>
                                                                                #{player.playerId}
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                ))
                                                            )}
                                                        </div>
                                                    )}
                                                </div>

                                                {/* Team Phone Number and Buttons Row - when self-scoring is enabled */}
                                                {selfScoringEnabled ? (
                                                    <div style={{ marginTop: 12 }}>
                                                        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
                                                            <div style={{ flex: 1 }}>
                                                                <label style={{ fontSize: 12, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>
                                                                    Team Phone Number <span style={{ color: 'var(--text-muted)', fontWeight: 'normal' }}>(optional)</span>
                                                                </label>
                                                                <input
                                                                    type="tel"
                                                                    value={teamPhone}
                                                                    onChange={(e) => {
                                                                        const formatted = formatPhoneInput(e.target.value, teamPhone);
                                                                        setTeamPhone(formatted);
                                                                        setTeamPhoneError('');
                                                                    }}
                                                                    onKeyDown={(e) => {
                                                                        if (e.key === 'Enter') {
                                                                            e.preventDefault();
                                                                            submitDoublesTeam();
                                                                        } else if (e.key === 'Escape') {
                                                                            cancelAddingDoublesTeam();
                                                                        }
                                                                    }}
                                                                    placeholder="(555) 123-4567"
                                                                    style={{
                                                                        width: '100%',
                                                                        padding: '8px 12px',
                                                                        fontSize: 14,
                                                                        borderRadius: 4,
                                                                        border: teamPhoneError ? '1px solid var(--danger)' : '1px solid var(--border-color)',
                                                                        boxSizing: 'border-box',
                                                                        backgroundColor: 'var(--bg-input)',
                                                                        color: 'var(--text-primary)'
                                                                    }}
                                                                />
                                                            </div>
                                                            <button
                                                                onClick={submitDoublesTeam}
                                                                style={{
                                                                    padding: '8px 14px',
                                                                    backgroundColor: 'var(--success)',
                                                                    color: '#fff',
                                                                    border: 'none',
                                                                    borderRadius: 4,
                                                                    cursor: 'pointer',
                                                                    fontSize: 13,
                                                                    fontWeight: '500',
                                                                    whiteSpace: 'nowrap'
                                                                }}
                                                            >
                                                                Add Team
                                                            </button>
                                                            <button
                                                                onClick={cancelAddingDoublesTeam}
                                                                style={{
                                                                    padding: '8px 12px',
                                                                    backgroundColor: 'var(--danger)',
                                                                    color: '#fff',
                                                                    border: 'none',
                                                                    borderRadius: 4,
                                                                    cursor: 'pointer',
                                                                    fontSize: 13
                                                                }}
                                                            >
                                                                Cancel
                                                            </button>
                                                        </div>
                                                        {teamPhoneError && (
                                                            <div style={{ fontSize: 12, color: 'var(--danger)', marginTop: 4 }}>
                                                                {teamPhoneError}
                                                            </div>
                                                        )}
                                                        <div style={{ fontSize: 10, color: 'var(--text-secondary)', marginTop: 4 }}>
                                                            By adding, team consents to receive SMS notifications. Message and data rates may apply.
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <div style={{ display: 'flex', gap: 6, marginTop: 12 }}>
                                                        <button
                                                            onClick={submitDoublesTeam}
                                                            style={{
                                                                padding: '8px 14px',
                                                                backgroundColor: 'var(--success)',
                                                                color: '#fff',
                                                                border: 'none',
                                                                borderRadius: 4,
                                                                cursor: 'pointer',
                                                                fontSize: 13,
                                                                fontWeight: '500'
                                                            }}
                                                        >
                                                            Add Team
                                                        </button>
                                                        <button
                                                            onClick={cancelAddingDoublesTeam}
                                                            style={{
                                                                padding: '8px 12px',
                                                                backgroundColor: 'var(--danger)',
                                                                color: '#fff',
                                                                border: 'none',
                                                                borderRadius: 4,
                                                                cursor: 'pointer',
                                                                fontSize: 13
                                                            }}
                                                        >
                                                            Cancel
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                            {newPlayerError && (
                                                <div style={{ fontSize: 12, color: 'var(--danger)', marginTop: 8 }}>
                                                    {newPlayerError}
                                                </div>
                                            )}
                                        </div>
                                    ) : (
                                        /* Singles Empty State Form */
                                        <div ref={addPlayerFormRef} style={{
                                            maxWidth: '450px',
                                            margin: '0 auto',
                                            textAlign: 'left',
                                            padding: '16px',
                                            backgroundColor: 'var(--bg-secondary)',
                                            borderRadius: 8,
                                            border: '2px solid var(--btn-primary)'
                                        }}>
                                            <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', flexWrap: selfScoringEnabled ? 'wrap' : 'nowrap' }}>
                                                <div style={{ flex: selfScoringEnabled ? '1 1 100%' : 1, position: 'relative' }}>
                                                    {selfScoringEnabled && (
                                                        <label style={{ fontSize: 11, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>
                                                            Player Name
                                                        </label>
                                                    )}
                                                    <input
                                                        type="text"
                                                        value={newPlayerValue}
                                                        onChange={(e) => {
                                                            setNewPlayerValue(e.target.value);
                                                            setShowSinglesDropdown(true);
                                                            setNewPlayerError('');
                                                            setSelectedSinglesUserId(null); // Clear userId when manually typing
                                                        }}
                                                        onFocus={() => setShowSinglesDropdown(true)}
                                                        onBlur={() => setTimeout(() => setShowSinglesDropdown(false), 200)}
                                                        onKeyDown={!selfScoringEnabled ? handleNewPlayerKeyPress : undefined}
                                                        placeholder={tournament?.isPublic ? "Name or search registered players..." : "Enter player name..."}
                                                        style={{
                                                            width: '100%',
                                                            padding: '10px 14px',
                                                            fontSize: 14,
                                                            borderRadius: 6,
                                                            border: newPlayerError ? '1px solid var(--danger)' : '1px solid var(--border-color)',
                                                            boxSizing: 'border-box',
                                                            backgroundColor: 'var(--bg-input)',
                                                            color: 'var(--text-primary)'
                                                        }}
                                                        autoFocus
                                                    />
                                                    {/* Singles Autocomplete Dropdown - Empty State */}
                                                    {showSinglesDropdown && tournament?.isPublic && (singlesSearchResults.length > 0 || isSearchingSingles) && (
                                                        <div style={{
                                                            position: 'absolute',
                                                            top: '100%',
                                                            left: 0,
                                                            right: 0,
                                                            backgroundColor: 'var(--bg-secondary)',
                                                            border: '1px solid var(--border-color)',
                                                            borderRadius: 4,
                                                            boxShadow: 'var(--shadow-md)',
                                                            zIndex: 100,
                                                            maxHeight: 180,
                                                            overflowY: 'auto'
                                                        }}>
                                                            {isSearchingSingles ? (
                                                                <div style={{ padding: 10, textAlign: 'center', color: 'var(--text-secondary)', fontSize: 12 }}>
                                                                    Searching...
                                                                </div>
                                                            ) : (
                                                                singlesSearchResults.map(player => (
                                                                    <div
                                                                        key={player.id}
                                                                        style={{
                                                                            padding: '8px 10px',
                                                                            cursor: 'pointer',
                                                                            fontSize: 13,
                                                                            borderBottom: '1px solid #eee',
                                                                            display: 'flex',
                                                                            alignItems: 'center',
                                                                            gap: 8
                                                                        }}
                                                                        onMouseDown={() => selectSinglesPlayer(player)}
                                                                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--bg-tertiary)'}
                                                                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'var(--bg-secondary)'}
                                                                    >
                                                                        {player.photoURL ? (
                                                                            <img
                                                                                src={player.photoURL}
                                                                                alt=""
                                                                                style={{ width: 24, height: 24, borderRadius: '50%' }}
                                                                            />
                                                                        ) : (
                                                                            <div style={{
                                                                                width: 24,
                                                                                height: 24,
                                                                                borderRadius: '50%',
                                                                                backgroundColor: 'var(--accent-primary)',
                                                                                color: '#fff',
                                                                                display: 'flex',
                                                                                alignItems: 'center',
                                                                                justifyContent: 'center',
                                                                                fontSize: 11,
                                                                                fontWeight: 'bold'
                                                                            }}>
                                                                                {player.displayName?.charAt(0).toUpperCase()}
                                                                            </div>
                                                                        )}
                                                                        <span style={{ flex: 1 }}>{player.displayName}</span>
                                                                        {player.playerId && (
                                                                            <span style={{
                                                                                fontSize: 11,
                                                                                color: 'var(--text-muted)',
                                                                                backgroundColor: 'var(--bg-tertiary)',
                                                                                padding: '2px 6px',
                                                                                borderRadius: 3,
                                                                                fontFamily: 'monospace'
                                                                            }}>
                                                                                #{player.playerId}
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                ))
                                                            )}
                                                        </div>
                                                    )}
                                                    {newPlayerError && !selfScoringEnabled && (
                                                        <div style={{ fontSize: 12, color: 'var(--danger)', marginTop: 4 }}>
                                                            {newPlayerError}
                                                        </div>
                                                    )}
                                                </div>

                                                {/* Phone Number Input - Only when self-scoring is enabled */}
                                                {selfScoringEnabled && (
                                                    <div style={{ flex: '1 1 100%', marginTop: 8 }}>
                                                        <label style={{ fontSize: 11, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>
                                                            Phone Number <span style={{ color: 'var(--text-muted)', fontWeight: 'normal' }}>(optional)</span>
                                                        </label>
                                                        <input
                                                            type="tel"
                                                            value={newPlayerPhone}
                                                            onChange={(e) => {
                                                                const formatted = formatPhoneInput(e.target.value, newPlayerPhone);
                                                                setNewPlayerPhone(formatted);
                                                                setNewPlayerPhoneError('');
                                                            }}
                                                            onKeyDown={(e) => {
                                                                if (e.key === 'Enter') {
                                                                    e.preventDefault();
                                                                    submitNewPlayer();
                                                                } else if (e.key === 'Escape') {
                                                                    cancelAddingPlayer();
                                                                }
                                                            }}
                                                            placeholder="(555) 123-4567"
                                                            style={{
                                                                width: '100%',
                                                                padding: '10px 14px',
                                                                fontSize: 14,
                                                                borderRadius: 6,
                                                                border: newPlayerPhoneError ? '1px solid var(--danger)' : '1px solid var(--border-color)',
                                                                boxSizing: 'border-box',
                                                                backgroundColor: 'var(--bg-input)',
                                                                color: 'var(--text-primary)'
                                                            }}
                                                        />
                                                        {newPlayerPhoneError && (
                                                            <div style={{ fontSize: 12, color: 'var(--danger)', marginTop: 4 }}>
                                                                {newPlayerPhoneError}
                                                            </div>
                                                        )}
                                                        <div style={{ fontSize: 10, color: 'var(--text-secondary)', marginTop: 4 }}>
                                                            By adding, player consents to receive SMS notifications. Message and data rates may apply.
                                                        </div>
                                                    </div>
                                                )}

                                                {/* Action Buttons */}
                                                <div style={{
                                                    display: 'flex',
                                                    gap: 8,
                                                    marginTop: selfScoringEnabled ? 12 : 0,
                                                    flex: selfScoringEnabled ? '1 1 100%' : 'none',
                                                    justifyContent: selfScoringEnabled ? 'flex-end' : 'flex-start'
                                                }}>
                                                    <button
                                                        onClick={submitNewPlayer}
                                                        style={{
                                                            padding: '10px 16px',
                                                            backgroundColor: 'var(--success)',
                                                            color: '#fff',
                                                            border: 'none',
                                                            borderRadius: 6,
                                                            cursor: 'pointer',
                                                            fontSize: 14,
                                                            fontWeight: '500'
                                                        }}
                                                    >
                                                        Add
                                                    </button>
                                                    <button
                                                        onClick={cancelAddingPlayer}
                                                        style={{
                                                            padding: '10px 16px',
                                                            backgroundColor: 'var(--danger)',
                                                            color: '#fff',
                                                            border: 'none',
                                                            borderRadius: 6,
                                                            cursor: 'pointer',
                                                            fontSize: 14
                                                        }}
                                                    >
                                                        Cancel
                                                    </button>
                                                </div>
                                            </div>
                                            {selfScoringEnabled && newPlayerError && (
                                                <div style={{ fontSize: 12, color: 'var(--danger)', marginTop: 8 }}>
                                                    {newPlayerError}
                                                </div>
                                            )}
                                            {tournament?.isPublic && !selfScoringEnabled && (
                                                <div style={{
                                                    fontSize: 11,
                                                    color: 'var(--text-secondary)',
                                                    marginTop: 10,
                                                    fontStyle: 'italic'
                                                }}>
                                                    Tip: Start typing to search for registered players
                                                </div>
                                            )}
                                        </div>
                                    )
                                )}
                            </div>
                        )}
                    </div>

                    {/* Footer */}
                    <div style={{
                        padding: '16px 24px',
                        borderTop: '1px solid var(--border-color)',
                        display: 'flex',
                        justifyContent: 'flex-end',
                        gap: 12
                    }}>
                        <button
                            onClick={onClose}
                            style={{
                                padding: '10px 24px',
                                backgroundColor: 'var(--accent-primary)',
                                color: '#fff',
                                border: 'none',
                                borderRadius: 6,
                                fontSize: 14,
                                fontWeight: '500',
                                cursor: 'pointer',
                                transition: 'background-color 0.2s'
                            }}
                            onMouseEnter={(e) => e.target.style.backgroundColor = 'var(--accent-secondary)'}
                            onMouseLeave={(e) => e.target.style.backgroundColor = 'var(--accent-primary)'}
                        >
                            Done
                        </button>
                    </div>
                </div>
            </div>
        </>
    );

    return ReactDOM.createPortal(modalContent, document.body);
}
